import { EditorEvent } from '@portal-core/project-files/events/editor-event.event';
import { NodeViewBase } from '@portal-core/text-editor/node-views/node-view.base';
import { GetPosForNodeView } from '@portal-core/text-editor/node-views/types/nodeview-get-pos.type';
import { BidiBridge } from '@portal-core/util/bidi-bridge';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import { Decoration, EditorView, NodeView } from 'prosemirror-view';

export class SlideshowNodeView extends NodeViewBase implements NodeView {
  private viewportDOM: HTMLElement;
  private sliderDOM: HTMLElement;
  private navDOM: HTMLElement;
  private controlsDOM: HTMLElement;
  private prevDOM: HTMLAnchorElement;
  private nextDOM: HTMLAnchorElement;
  private pagerDOM: HTMLElement;

  private slideCount: number = 0;
  private slideIndex: number = 0;

  private onPrevClicked: (event: Event) => void;
  private onNextClicked: (event: Event) => void;
  private onPrevKeydown: (event: KeyboardEvent) => void;
  private onNextKeydown: (event: KeyboardEvent) => void;
  private onPagerClicked: (event: Event) => void;
  private onPagerKeydown: (event: KeyboardEvent) => void;
  private onSelectionEnteredSlide: (event: Event) => void;

  constructor(
    node: ProseMirrorNode,
    editorView: EditorView,
    getPos: GetPosForNodeView,
    decorations: Decoration[],
    nodeViewBridge: BidiBridge<EditorEvent, EditorEvent>
  ) {
    super(node, editorView, getPos, decorations, nodeViewBridge);

    // Build the DOM structure
    this.viewportDOM = this.dom.appendChild(document.createElement('mc-lwe-slideshow-viewport'));

    this.sliderDOM = this.viewportDOM.appendChild(document.createElement('mc-lwe-slideshow-slider'));
    this.contentDOM = this.sliderDOM;

    this.navDOM = this.dom.appendChild(document.createElement('mc-lwe-slideshow-nav'));
    this.navDOM.setAttribute('contenteditable', 'false');
    this.navDOM.setAttribute('draggable', 'false');

    this.controlsDOM = this.navDOM.appendChild(document.createElement('mc-lwe-slideshow-controls'));
    this.controlsDOM.setAttribute('contenteditable', 'false');
    this.controlsDOM.setAttribute('draggable', 'false');

    this.prevDOM = this.controlsDOM.appendChild(document.createElement('a'));
    this.prevDOM.href = '#';
    this.prevDOM.classList.add('mc-lwe-slideshow-prev');
    this.prevDOM.setAttribute('role', 'button');
    this.prevDOM.tabIndex = 0;
    this.prevDOM.setAttribute('contenteditable', 'false');
    this.prevDOM.setAttribute('draggable', 'false');

    this.nextDOM = this.controlsDOM.appendChild(document.createElement('a'));
    this.nextDOM.href = '#';
    this.nextDOM.classList.add('mc-lwe-slideshow-next');
    this.nextDOM.setAttribute('role', 'button');
    this.nextDOM.tabIndex = 0;
    this.nextDOM.setAttribute('contenteditable', 'false');
    this.nextDOM.setAttribute('draggable', 'false');

    this.pagerDOM = this.navDOM.appendChild(document.createElement('mc-lwe-slideshow-pager'));
    this.pagerDOM.setAttribute('contenteditable', 'false');
    this.pagerDOM.setAttribute('draggable', 'false');

    // Listen for nav click events
    this.onPrevClicked = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      this.setSlideIndex(this.slideIndex === 0 ? this.slideCount - 1 : this.slideIndex - 1);
    };

    this.onNextClicked = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      this.setSlideIndex(this.slideIndex === this.slideCount - 1 ? 0 : this.slideIndex + 1);
    };

    this.prevDOM.addEventListener('click', this.onPrevClicked);
    this.nextDOM.addEventListener('click', this.onNextClicked);

    // Listen to nav keydown events for accessibility
    this.onPrevKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        this.onPrevClicked(event);
      }
    };

    this.onNextKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        this.onNextClicked(event);
      }
    };

    this.prevDOM.addEventListener('keydown', this.onPrevKeydown);
    this.nextDOM.addEventListener('keydown', this.onNextKeydown);

    // Listen to page bullet click/keydown events
    this.onPagerClicked = (event: Event) => {
      const bullet = this.closestBullet(event.target);
      if (!bullet) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      const index = this.getBulletIndex(bullet);
      if (index != null) {
        this.setSlideIndex(index);
      }
    };

    this.onPagerKeydown = (event: KeyboardEvent) => {
      const bullet = this.closestBullet(event.target);
      if (!bullet) {
        return;
      }

      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      const index = this.getBulletIndex(bullet);
      if (index != null) {
        this.setSlideIndex(index);
      }
    };

    this.pagerDOM.addEventListener('click', this.onPagerClicked);
    this.pagerDOM.addEventListener('keydown', this.onPagerKeydown);

    // slideshow.plugin dispatches the selection-in-slide event when selection enters a slide
    this.onSelectionEnteredSlide = (event: Event) => {
      const detail = (event as CustomEvent<{ slideIndex: number }>)?.detail;
      const index = detail?.slideIndex;
      if (typeof index === 'number') {
        this.setSlideIndex(index);
      }
    };
    this.dom.addEventListener('selection-in-slide', this.onSelectionEnteredSlide as EventListener);

    // Sync the node
    this.syncNode(node);
  }

  destroy() {
    this.prevDOM?.removeEventListener('click', this.onPrevClicked);
    this.nextDOM?.removeEventListener('click', this.onNextClicked);
    this.prevDOM?.removeEventListener('keydown', this.onPrevKeydown);
    this.nextDOM?.removeEventListener('keydown', this.onNextKeydown);
    this.pagerDOM?.removeEventListener('click', this.onPagerClicked);
    this.pagerDOM?.removeEventListener('keydown', this.onPagerKeydown);
    this.dom?.removeEventListener('selection-in-slide', this.onSelectionEnteredSlide as EventListener);
    super.destroy();
  }

  stopEvent(event: Event): boolean {
    return this.navDOM?.contains(event.target as HTMLElement);
  }

  syncNode(node: ProseMirrorNode) {
    this.slideCount = node.childCount ?? 0;
    if (this.slideCount === 0) {
      this.slideIndex = 0;
    } else if (this.slideIndex >= this.slideCount) {
      this.slideIndex = this.slideCount - 1;
    }

    this.syncPager();
    this.updateActiveBullet();
  }

  private syncPager() {
    const existing = Array.from(this.pagerDOM.querySelectorAll<HTMLElement>('mc-lwe-slideshowbullet'));

    // Rebuild if counts differ (simple + safe)
    if (existing.length !== this.slideCount) {
      this.pagerDOM.replaceChildren();

      for (let index = 0; index < this.slideCount; index++) {
        const item = this.pagerDOM.appendChild(document.createElement('mc-lwe-slideshow-pager-item'));
        item.setAttribute('contenteditable', 'false');
        item.setAttribute('draggable', 'false');

        const bullet = item.appendChild(document.createElement('mc-lwe-slideshowbullet'));
        bullet.setAttribute('data-slide-index', String(index));
        bullet.setAttribute('role', 'button');
        bullet.setAttribute('tabindex', '0');
        bullet.setAttribute('contenteditable', 'false');
        bullet.setAttribute('draggable', 'false');
      }
    }
  }

  private updateActiveBullet() {
    const bullets = Array.from(this.pagerDOM.querySelectorAll<HTMLElement>('mc-lwe-slideshowbullet'));
    for (const bullet of bullets) {
      const index = this.getBulletIndex(bullet);
      bullet.classList.toggle('active', index === this.slideIndex);
    }
  }

  private setSlideIndex(index: number, scroll: boolean = true) {
    if (!Number.isFinite(index)) {
      return;
    }

    const clamped = this.slideCount <= 0 ? 0 : Math.max(0, Math.min(index, this.slideCount - 1));
    if (clamped === this.slideIndex) {
      return;
    }

    this.slideIndex = clamped;
    this.updateActiveBullet();

    if (scroll) {
      requestAnimationFrame(() => this.scrollToSlide(this.slideIndex));
    }
  }

  private scrollToSlide(index: number) {
    // Check if this exists because scrolling is asynchronous and the node view may have been destroyed in the meantime
    if (!this.sliderDOM) {
      return;
    }

    if (index >= 0 && index < this.sliderDOM.children.length) {
      this.sliderDOM.scrollTo({ left: index * this.sliderDOM.clientWidth, behavior: 'smooth' });
    }
  }

  private closestBullet(target: EventTarget): HTMLElement {
    const el = target as HTMLElement;
    return el?.closest?.('mc-lwe-slideshowbullet') ?? null;
  }

  private getBulletIndex(bullet: HTMLElement): number {
    const raw = bullet.getAttribute('data-slide-index');
    // == on purpose to detect both null and undefined
    if (raw == null) {
      return null;
    }

    const index = Number(raw);
    return Number.isFinite(index) ? index : null;
  }

}
